import { Line } from "@react-three/drei"
import { useFrame } from "@react-three/fiber"
import { useMemo, useRef } from "react"
import { Group, Mesh, QuadraticBezierCurve3, Vector3 } from "three"
import type { LandingData } from "../data/types"
import { scenePresence, sectionFocus, type LandingMotionRef } from "../lib/landing-motion"
import { applyGroupOpacity, latLon } from "../lib/three"
import { SceneText } from "./SceneText"

type ArcRef = { material?: { opacity?: number } } | null

export function Globe({ data, motion }: { data: LandingData; motion: LandingMotionRef }) {
  const group = useRef<Group>(null)
  const dotRefs = useRef<Array<Mesh | null>>([])
  const arcRefs = useRef<Array<ArcRef>>([])
  const arcs = useMemo(() => makeArcs(data), [data])

  useFrame((state, delta) => {
    const section = motion.current.section
    const t = state.clock.elapsedTime
    const hero = scenePresence(section, -0.25, 1.6, 0.42)
    const closing = scenePresence(section, 5.42, 7.3, 0.52)
    const presence = Math.max(hero, closing)
    const finalResolve = sectionFocus(section, 6.65, 1.2)
    const labelBoost = sectionFocus(section, 0.55, 0.62)

    if (group.current) {
      group.current.position.set(2.42 - finalResolve * 2.14 - sectionFocus(section, 1.35, 1.2) * 0.42, -0.05 - finalResolve * 0.08, -0.18 + finalResolve * 0.26)
      const scale = 1.04 + hero * 0.58 + finalResolve * 0.34
      group.current.scale.setScalar(scale)
      group.current.rotation.y += delta * (0.16 + presence * 0.06 + Math.min(0.1, Math.abs(motion.current.velocity) * 24))
      applyGroupOpacity(group.current, presence)
    }

    for (let i = 0; i < data.exchanges.length; i++) {
      const dot = dotRefs.current[i]
      if (!dot) continue
      const ex = data.exchanges[i]
      const base = 0.7 + ex.pulse * 1.8
      const breathe = 1 + Math.sin(t * 1.5 + i * 0.7) * 0.22 * Math.max(0.2, ex.pulse)
      dot.scale.setScalar(base * breathe * (1 + labelBoost * 0.18))
    }

    for (let i = 0; i < arcRefs.current.length; i++) {
      const arc = arcRefs.current[i]
      if (!arc?.material || typeof arc.material.opacity !== "number") continue
      arc.material.opacity = presence * (0.1 + (Math.sin(t * 0.85 + i * 0.55) * 0.5 + 0.5) * (0.32 + labelBoost * 0.22))
    }
  })

  return (
    <group ref={group}>
      <mesh>
        <sphereGeometry args={[1.42, 72, 36]} />
        <meshBasicMaterial color="#0b8171" wireframe transparent opacity={0.48} />
      </mesh>
      {data.exchanges.map((exchange, i) => (
        <mesh
          key={exchange.code}
          position={latLon(exchange.lat, exchange.lon, 1.48)}
          ref={(el) => {
            dotRefs.current[i] = el
          }}
        >
          <sphereGeometry args={[0.035, 16, 12]} />
          <meshBasicMaterial color="#56e6ce" />
        </mesh>
      ))}
      {data.exchanges.slice(0, 7).map((exchange) => {
        const point = latLon(exchange.lat, exchange.lon, 1.68)
        return (
          <SceneText key={`${exchange.code}-label`} position={[point.x, point.y + 0.08, point.z]} fontSize={0.105} color="#f2f5f4" fillOpacity={0.34} outlineWidth={0.006} outlineColor="#020404">
            {exchange.code}
          </SceneText>
        )
      })}
      {arcs.map((points, i) => (
        <Line
          key={i}
          points={points}
          color="#14d8b4"
          transparent
          opacity={0.22}
          ref={(el) => {
            arcRefs.current[i] = el as ArcRef
          }}
        />
      ))}
    </group>
  )
}

function makeArcs(data: LandingData) {
  const pairs = [
    ["NYSE", "LSE"], ["NASDAQ", "TSE"], ["LSE", "HKEX"], ["XETRA", "NSE"],
    ["NYSE", "TSX"], ["HKEX", "ASX"], ["SIX", "NASDAQ"], ["EURONEXT", "KRX"],
    ["TSE", "ASX"], ["NSE", "LSE"], ["TSX", "XETRA"], ["NASDAQ", "HKEX"],
  ]
  const byCode = Object.fromEntries(data.exchanges.map((x) => [x.code, x]))
  return pairs.flatMap(([a, b]) => {
    const start = byCode[a]
    const end = byCode[b]
    if (!start || !end) return []
    const from = latLon(start.lat, start.lon, 1.52)
    const to = latLon(end.lat, end.lon, 1.52)
    const mid = from.clone().add(to).multiplyScalar(0.5).normalize().multiplyScalar(2.05)
    return [new QuadraticBezierCurve3(from, mid, to).getPoints(38) as Vector3[]]
  })
}
